import type { BoxProps } from '@shopify/polaris';
import type { IInfoPanelItem } from '../types';

import { Box, InlineStack, Text, Icon } from '@shopify/polaris';
import { CheckIcon } from '@shopify/polaris-icons';
import { useToggle } from '~/hooks/useToggle';
import { useCallback } from 'react';

import styles from '../GInfoPanel.module.css';

interface GInfoPanelSingleItemProps {
  item: IInfoPanelItem;
  isSelected: boolean;
  selectedBackground?: BoxProps['background'];
  onSelect: (id: string) => void;
  onHover?: (id: string) => void;
}

export const GInfoPanelSingleItem: React.FC<GInfoPanelSingleItemProps> = ({
  item,
  isSelected,
  selectedBackground,
  onSelect,
  onHover,
}) => {
  const { value: isHovered, setTrue, setFalse } = useToggle(false);

  const backgroundSelected = selectedBackground ?? 'bg-surface-secondary-selected';
  const backgroundHover = isHovered && !item.disabled ? 'bg-surface-secondary-hover' : undefined;

  const handleClick = useCallback(() => {
    if (item.disabled) return;

    onSelect(item.id);
  }, [item.id, item.disabled, onSelect]);

  const handleMouseEnter = useCallback(() => {
    if (item.disabled) return;

    onHover?.(item.id);
    setTrue();
  }, [item.id, item.disabled, onHover, setTrue]);

  const handleMouseLeave = useCallback(() => {
    if (item.disabled) return;

    setFalse();
  }, [item.disabled, setFalse]);

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: item.disabled ? 'not-allowed' : 'pointer' }}
      {...(!item.disabled && { className: styles.ItemHover })}
    >
      <Box
        padding="150"
        borderRadius="200"
        background={isSelected ? backgroundSelected : backgroundHover}
        color={item.checkedIcon || item.disabled ? 'text-disabled' : undefined}
      >
        <InlineStack gap="150" blockAlign="center" wrap={false}>
          {item.icon && (
            <Box>
              <Icon source={item.icon} />
            </Box>
          )}
          <Box width="100%" overflowX="hidden">
            {typeof item.label === 'string' ? (
              <Text
                as="span"
                variant="bodyMd"
                truncate
                fontWeight={isSelected ? 'semibold' : 'regular'}
              >
                {item.label}
              </Text>
            ) : (
              item.label
            )}
          </Box>
          {(isSelected || item.checkedIcon) && (
            <Box>
              <Icon source={CheckIcon} />
            </Box>
          )}
        </InlineStack>
      </Box>
    </div>
  );
};
